import Layout from 'components/layout';

import React, { FC } from 'react';

import { graphql } from 'gatsby';
import { getI18nData } from '@lucid/utils';
import {
  Box,
  Container,
  Carousel,
  CarouselItems,
  CarouselHeroSlider,
  CarouselLogo,
  Quote,
  QuoteCompanyProps,
  ComponentImage,
  FeatureGrid
} from '@lucid/components-react';
import { mqMin, LucidTheme } from '@lucid/theme';
import {
  Drupal_NodeTwentyoneHomepage,
  Drupal_ParagraphQuote,
  Drupal_ParagraphCarouselHero,
  Drupal_ParagraphCarouselLogo,
  Drupal_ParagraphFeaturedGridSection,
  Drupal_ParagraphPreFooterSection,
  Drupal_ParagraphCarouselSection,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphQuoteAttribution,
  Drupal_ParagraphCompanyInfo,
  Drupal_ParagraphSeoImage,
  Drupal_ParagraphCtaLinkButton,
  Drupal_FieldNodeTwentyoneHomepageFieldBackNForthSection,
  Drupal_ParagraphCarouselItem,
  Drupal_TaxonomyTermAnimations
} from 'generated/drupal-graphql';

import BackNForth from 'components/back-n-forth/back-n-forth';
import PreFooterCTA from 'components/pre-footer-cta/pre-footer-cta';
import { PageTemplateProps, isDefined, CarouselHeroSliderLink, CarouselHeroSliderItems } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';
import { mapFeaturedGridItems } from 'src/utils/mapComponents';

const backgroundAccents = [
  {
    desktop: 'https://cdn-cashy-static-assets.lucidchart.com/marketing/chart/homepage/circle-background-accent.png',
    mobile: 'https://cdn-cashy-static-assets.lucidchart.com/marketing/chart/homepage/circle-mobile-background-accent.png'
  },
  {
    desktop: 'https://cdn-cashy-static-assets.lucidchart.com/marketing/chart/homepage/hexagon-background-accent.png',
    mobile: 'https://cdn-cashy-static-assets.lucidchart.com/marketing/chart/homepage/hexagon-mobile-background-accent.png'
  },
  {
    desktop: 'https://cdn-cashy-static-assets.lucidchart.com/marketing/chart/homepage/square-background-accent.png',
    mobile: 'https://cdn-cashy-static-assets.lucidchart.com/marketing/chart/homepage/square-mobile-background-accent.png'
  }
];

const TwentyoneHomepage: FC<PageTemplateProps> = ({ data, pageContext, location }) => {
  const { langPrefix, pageType, nid, shouldHideSsr, i18nKey } = pageContext;
  const { drupal, i18nData } = data;
  const node = drupal.nodeById as Drupal_NodeTwentyoneHomepage;
  const { entityMetatags, entitySchemaMetatags, entityTranslations } = getEntityFields(node);
  const {
    fieldHeader: fieldCarouselHeroHeader,
    fieldTitlePiece,
    fieldSubheader: fieldCarouselHeroSubheader,
    fieldCarouselHeroLink,
    fieldCarouselHeroItems
  } = node.fieldCarouselHero?.entity as Drupal_ParagraphCarouselHero;
  const {
    fieldHeader: fieldCarouselLogoHeader,
    fieldCarouselLogo
  } = node.fieldCarouselLogos?.entity as Drupal_ParagraphCarouselLogo;
  const backNForthSections =
    node.fieldBackNForthSection!.length > 0
      ? (node.fieldBackNForthSection as Drupal_FieldNodeTwentyoneHomepageFieldBackNForthSection[])
      : null;
  const {
    fieldAttributionData,
    fieldCompanyInfo,
    fieldQuoteCtaLink,
    fieldQuote
  } = node.fieldQuoteSection?.entity as Drupal_ParagraphQuote;
  const fieldQuoteAuthor = fieldAttributionData?.entity as Drupal_ParagraphQuoteAttribution;
  const fieldQuoteCompany = fieldCompanyInfo?.entity as Drupal_ParagraphCompanyInfo;
  const fieldQuoteCompanyLogo = fieldQuoteCompany.fieldCompanyLogo?.entity as Drupal_ParagraphSeoImage;
  const fieldQuoteCta = fieldQuoteCtaLink?.entity as Drupal_ParagraphCtaLinkButton;

  const featuredGrid = node.fieldFeaturedGridSection?.entity as Drupal_ParagraphFeaturedGridSection;
  const featuredGridItems = featuredGrid?.fieldFeaturedGridItems?.map(fg => mapFeaturedGridItems(fg));

  const fieldBottomCarousel = node.fieldCarousel?.entity as Drupal_ParagraphCarouselSection;
  const fieldBottomCarouselCta = fieldBottomCarousel.fieldCtaLinkButton?.entity as Drupal_ParagraphCtaLinkButton;
  const bottomCarouselItems = fieldBottomCarousel.fieldItems?.reduce<CarouselItems[]>((previous, current) => {
    const item = current?.entity as Drupal_ParagraphCarouselItem;
    const animationData = item.fieldAnimation?.entity as Drupal_TaxonomyTermAnimations;
    const itemImage = item.fieldSectionImage?.entity as Drupal_ParagraphSeoImage;
    return [
      ...previous,
      {
        animation: {
          data: animationData?.fieldAnimationData
        },
        heading: item.fieldTitle!,
        subheading: item.fieldSubtitle!,
        image: {
          imageAlt: itemImage.fieldAltText! || '',
          imageSrc: itemImage.fieldImageLink!
        },
        footerText: item.fieldCarouselFooterText!
      }
    ];
  }, []);
  const bottomCarousel = {
    cta: {
      displayAsButton: fieldBottomCarouselCta?.fieldDisplayAsButton || false,
      hasArrow: fieldBottomCarouselCta?.fieldLinkHasArrow || false,
      isSecondary: fieldBottomCarouselCta?.fieldIsSecondary || false,
      linkText: fieldBottomCarouselCta?.fieldCta?.title!,
      linkUrl: fieldBottomCarouselCta?.fieldCta?.url?.path!
    },
    heading: fieldBottomCarousel.fieldTitle!,
    subheading: fieldBottomCarousel.fieldSubtitle!
  };
  const preFooterCta = node.fieldPreFooterSection?.entity as Drupal_ParagraphPreFooterSection;
  const preFooterCtas = preFooterCta
    ? preFooterCta
      .fieldCtaButton!.filter(isDefined)
      .map(button => button.entity as Drupal_ParagraphCtaButton)
    : null;
  const carouselLogos = fieldCarouselLogo?.reduce<ComponentImage[]>((previous, current) => {
    const currentImage = current?.entity as Drupal_ParagraphSeoImage;
    return [
      ...previous,
      {
        imageSrc: currentImage.fieldImageLink as string,
        imageAlt: currentImage.fieldAltText as string || ''
      }
    ];
  }, []);

  const quoteData = {
    author: {
      firstName: fieldQuoteAuthor.fieldFirstName,
      lastName: fieldQuoteAuthor.fieldLastName,
      title: fieldQuoteAuthor.fieldTitle
    },
    company: {
      name: fieldQuoteCompany.fieldCompanyName,
      logo: {
        imageSrc: fieldQuoteCompanyLogo.fieldImageLink,
        imageAlt: fieldQuoteCompanyLogo.fieldAltText || '',
      }
    },
    quote: fieldQuote,
    cta: {
      hasArrow: fieldQuoteCta.fieldLinkHasArrow,
      displayAsButton: fieldQuoteCta.fieldDisplayAsButton,
      url: {
        path: fieldQuoteCta.fieldCta?.url?.path
      },
      title: fieldQuoteCta.fieldCta?.title
    }
  };

  const i18n = getI18nData(i18nData, i18nKey);

  return (
    <Layout
      entityTranslations={entityTranslations}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
      schema={entitySchemaMetatags}
      shouldHideSsr={shouldHideSsr}
    >
      <Box as="main" css={{ label: pageType }}>
        <CarouselHeroSlider
          hasSlider
          shouldMobileImagesOverflow
          ariaLabel={i18n('mktg_template_aria_label_slide_title')}
          cta={fieldCarouselHeroLink as CarouselHeroSliderLink}
          header={
            fieldTitlePiece && fieldTitlePiece.length > 0
              ? fieldTitlePiece as string[]
              : fieldCarouselHeroHeader!
          }
          items={fieldCarouselHeroItems as CarouselHeroSliderItems[]}
          sliderTime={8000}
          subheader={fieldCarouselHeroSubheader!}
        />
        <CarouselLogo header={fieldCarouselLogoHeader} logos={carouselLogos} />
        {backNForthSections && (
          <BackNForth
            shouldStartOnLeft
            backgroundAccents={backgroundAccents}
            css={(theme: LucidTheme) => ({
              [mqMin.tablet]: {
                paddingTop: theme.space.s104
              }
            })}
            sections={backNForthSections}
          />
        )}
        <Quote
          author={quoteData.author}
          company={quoteData.company as QuoteCompanyProps}
          css={(theme: LucidTheme) => ({
            backgroundColor: theme.colors.coolGrey[0]
          })}
          cta={quoteData.cta}
          quote={fieldQuote}
        />
        <Container>
          {featuredGridItems?.length && (
            <Box
              mb={7}
              mt={7}
            >
              <FeatureGrid
                items={featuredGridItems}
                linkProps={{
                  variant: 'secondary',
                  iconColor: 'indigo.60'
                }}
                sectionSubtitle={featuredGrid.fieldSubtitle}
                sectionTitle={featuredGrid.fieldTitle}
                variant="masonry"
              />
            </Box>
          )}
          {bottomCarousel && (
            <Carousel
              cta={bottomCarousel.cta}
              heading={bottomCarousel.heading}
              items={bottomCarouselItems!}
              subheading={bottomCarousel.subheading}
            />
          )}
        </Container>
        {preFooterCta && (
          <PreFooterCTA ctas={preFooterCtas} header={preFooterCta.fieldHeading} subheader={preFooterCta.fieldSubheader} />
        )}
      </Box>
    </Layout>
  );
};

export default TwentyoneHomepage;

export const query = graphql`
  query($nid: String!, $i18nKey: String!, $LanguageId: Drupal_LanguageId!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeTwentyoneHomepage {
          ...entityFields
          fieldCarouselHero {
            entity {
              ... on Drupal_ParagraphCarouselHero {
                fieldHeader
                fieldTitlePiece
                fieldSubheader
                fieldCarouselHeroLink {
                  entity {
                    ... on Drupal_ParagraphCtaLinkButton {
                      fieldLinkHasArrow
                      fieldDisplayAsButton
                      fieldIsSecondary
                      fieldCta {
                        url {
                          path
                        }
                        title
                      }
                    }
                  }
                }
                fieldCarouselHeroItems {
                  entity {
                    ... on Drupal_ParagraphCarouselHeroItems {
                      fieldHeader
                      fieldSubheader
                      fieldImage
                      fieldImageAlt
                      fieldIsLinkJumplink
                      fieldCarouselHeroItemLink {
                        entity {
                          ... on Drupal_ParagraphCtaLinkButton {
                            fieldCta {
                              url {
                                path
                              }
                              title
                            }
                          }
                        }
                      }
                      fieldAnimation {
                        entity {
                          ... on Drupal_TaxonomyTermAnimations {
                            fieldAnimationData
                            fieldAltDescription
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          fieldCarouselLogos {
            entity {
              ... on Drupal_ParagraphCarouselLogo {
                fieldHeader
                fieldCarouselLogo {
                  ... on Drupal_FieldParagraphCarouselLogoFieldCarouselLogo {
                    entity {
                      ... on Drupal_ParagraphSeoImage {
                        fieldAltText
                        fieldImageLink
                      }
                    }
                  }
                }
              }
            }
          }
          fieldBackNForthSection {
            entity {
              ... on Drupal_ParagraphBackNForthSection {
                fieldCta {
                  url {
                    path
                  }
                  title
                }
                fieldLogo {
                  entity {
                    ... on Drupal_ParagraphSeoImage {
                      fieldImageLink
                      fieldAltText
                    }
                  }
                }
                fieldTitle
                fieldCtaButton {
                  entity {
                    ... on Drupal_ParagraphCtaLinkButton {
                      fieldCta {
                        url {
                          path
                        }
                        title
                      }
                    }
                  }
                }
                fieldSectionImage {
                  entity {
                    ... on Drupal_ParagraphSeoImage {
                      fieldImageLink
                      fieldAltText
                    }
                  }
                }
                fieldSectionAltText
                fieldBackgroundImage
                fieldSectionDescription {
                  processed
                }
              }
            }
          }
          fieldQuoteSection {
            entity {
              ... on Drupal_ParagraphQuote {
                fieldQuote
                fieldQuoteCtaLink {
                  entity {
                    ... on Drupal_ParagraphCtaLinkButton {
                      fieldLinkHasArrow
                      fieldDisplayAsButton
                      fieldCta {
                        url {
                          path
                        }
                        title
                      }
                    }
                  }
                }
                fieldAttributionData {
                  entity {
                    ... on Drupal_ParagraphQuoteAttribution {
                      fieldFirstName
                      fieldLastName
                      fieldTitle
                    }
                  }
                }
                fieldCompanyInfo {
                  entity {
                    ... on Drupal_ParagraphCompanyInfo {
                      fieldCompanyLogo {
                        entity {
                          ... on Drupal_ParagraphSeoImage {
                            fieldImageLink
                            fieldAltText
                          }
                        }
                      }
                      fieldCompanyName
                    }
                  }
                }
              }
            }
          }
          fieldFeaturedGridSection {
            entity {
							... on Drupal_ParagraphFeaturedGridSection {
                fieldTitle
                fieldSubtitle
                fieldFeaturedGridItems {
                  entity {
										... on Drupal_ParagraphFeaturedGridItem {
                      fieldTitle
                      fieldItemSectionDescription {
                        processed
                      }
                      fieldSectionImage {
                        entity {
                          ... on Drupal_ParagraphSeoImage {
                            fieldAltText
                            fieldImageLink
                          }
                        }
                      }
                      fieldCta {
                        url {
                          ...getLinkPath
                        }
                        title
                      }
                    }
                  }
                }
              }
            }
          }
          fieldCarousel {
            entity {
              ... on Drupal_ParagraphCarouselSection {
                fieldTitle
                fieldSubtitle
                fieldCtaLinkButton {
                  entity {
                    ... on Drupal_ParagraphCtaLinkButton {
                      fieldLinkHasArrow
                      fieldDisplayAsButton
                      fieldCta {
                        url {
                          path
                        }
                        title
                      }
                    }
                  }
                }
                fieldItems {
                  entity {
                    ... on Drupal_ParagraphCarouselItem {
                      fieldTitle
                      fieldCarouselFooterText
                      fieldSectionImage {
                        entity {
                          ... on Drupal_ParagraphSeoImage {
                            fieldAltText
                            fieldImageLink
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          fieldPreFooterSection {
            entity {
              ... on Drupal_ParagraphPreFooterSection {
                fieldHeading
                fieldCtaButton {
                  entity {
                    ... on Drupal_ParagraphCtaButton {
                      fieldButtonText
                      fieldButtonLink
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_template_aria_label_slide_title
          }
        }
      }
    }
  }
`;
